import React, { useState, useEffect } from 'react';
import { adminAPI } from '../api';
import { 
    Trophy, Plus, Trash2, ChevronRight, ChevronLeft, Users, 
    Edit2, Save, X, Award
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

const TournamentManager = ({ token, users, onError, onSuccess }) => {
    // View state: 'list' | 'tournament' | 'round' | 'group'
    const [view, setView] = useState('list');
    const [loading, setLoading] = useState(false);
    
    // Data
    const [tournaments, setTournaments] = useState([]);
    const [currentTournament, setCurrentTournament] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [currentRound, setCurrentRound] = useState(null);
    const [groups, setGroups] = useState([]);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [groupScores, setGroupScores] = useState(null);
    
    // Modals
    const [showCreateTournament, setShowCreateTournament] = useState(false);
    const [showCreateRound, setShowCreateRound] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [showAddMember, setShowAddMember] = useState(false);
    const [showEditScores, setShowEditScores] = useState(false);
    
    // Form data
    const [tournamentName, setTournamentName] = useState('');
    const [roundName, setRoundName] = useState('');
    const [groupName, setGroupName] = useState('');
    const [numGames, setNumGames] = useState(3);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [editingGameNumber, setEditingGameNumber] = useState(1);
    const [scoreInputs, setScoreInputs] = useState({});

    useEffect(() => {
        loadTournaments();
    }, []);

    const loadTournaments = async () => {
        setLoading(true);
        try {
            const data = await adminAPI.getTournaments(token);
            setTournaments(data);
        } catch (err) {
            onError('Failed to load tournaments');
        } finally {
            setLoading(false);
        }
    };

    const loadRounds = async (tournamentId) => {
        setLoading(true);
        try {
            const data = await adminAPI.getTournamentRounds(token, tournamentId);
            setCurrentTournament(data.tournament);
            setRounds(data.rounds);
            setView('tournament');
        } catch (err) {
            onError('Failed to load rounds');
        } finally {
            setLoading(false);
        }
    };

    const loadGroups = async (roundId) => {
        setLoading(true);
        try {
            const data = await adminAPI.getTournamentGroups(token, roundId);
            setCurrentRound(data.round);
            setGroups(data.groups);
            setView('round');
        } catch (err) {
            onError('Failed to load groups');
        } finally {
            setLoading(false);
        }
    };

    const loadGroupScores = async (group) => {
        setLoading(true);
        try {
            const data = await adminAPI.getTournamentScores(token, group.id);
            setCurrentGroup(group);
            setGroupScores(data);
            setView('group');
        } catch (err) {
            onError('Failed to load scores');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTournament = async () => {
        if (!tournamentName.trim()) return;
        try {
            await adminAPI.createTournament(token, tournamentName);
            onSuccess('Tournament created');
            setTournamentName('');
            setShowCreateTournament(false);
            loadTournaments();
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to create tournament');
        }
    };

    const handleDeleteTournament = async (id) => {
        if (!window.confirm('Delete this tournament and all its data?')) return;
        try {
            await adminAPI.deleteTournament(token, id);
            onSuccess('Tournament deleted');
            loadTournaments();
        } catch (err) {
            onError('Failed to delete tournament');
        }
    };

    const handleCreateRound = async () => {
        if (!roundName.trim() || !currentTournament) return;
        try {
            await adminAPI.createTournamentRound(token, currentTournament.id, roundName);
            onSuccess('Round created');
            setRoundName('');
            setShowCreateRound(false);
            loadRounds(currentTournament.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to create round');
        }
    };

    const handleDeleteRound = async (roundId) => {
        if (!window.confirm('Delete this round and all its groups?')) return;
        try {
            await adminAPI.deleteTournamentRound(token, currentTournament.id, roundId);
            onSuccess('Round deleted');
            loadRounds(currentTournament.id);
        } catch (err) {
            onError('Failed to delete round');
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim() || !currentRound) return;
        try {
            await adminAPI.createTournamentGroup(token, currentRound.id, groupName, numGames);
            onSuccess('Group created');
            setGroupName('');
            setNumGames(3);
            setShowCreateGroup(false);
            loadGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to create group');
        }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            await adminAPI.deleteTournamentGroup(token, groupId);
            onSuccess('Group deleted');
            loadGroups(currentRound.id);
        } catch (err) {
            onError('Failed to delete group');
        }
    };

    const handleAddMember = async () => {
        if (!selectedUserId || !currentGroup) return;
        try {
            await adminAPI.addTournamentGroupMember(token, currentGroup.id, selectedUserId);
            onSuccess('Member added');
            setSelectedUserId('');
            setShowAddMember(false);
            loadGroupScores(currentGroup);
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to add member');
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!window.confirm('Remove this member?')) return;
        try {
            await adminAPI.removeTournamentGroupMember(token, currentGroup.id, userId);
            onSuccess('Member removed');
            loadGroupScores(currentGroup);
        } catch (err) {
            onError('Failed to remove member');
        }
    };

    const openEditScores = (gameNum) => {
        setEditingGameNumber(gameNum);
        // Pre-fill existing scores
        const existingScore = groupScores?.scores?.find(s => s.game_number === gameNum);
        const inputs = {};
        groupScores?.members?.forEach(m => {
            const entry = existingScore?.entries?.find(e => e.user_id === m.id);
            inputs[m.id] = entry ? entry.score.toString() : '0';
        });
        setScoreInputs(inputs);
        setShowEditScores(true);
    };

    const handleSaveScores = async () => {
        if (!currentGroup) return;
        try {
            const entries = Object.entries(scoreInputs).map(([userId, score]) => ({
                user_id: userId,
                score: parseFloat(score) || 0
            }));
            await adminAPI.addTournamentScore(token, currentGroup.id, editingGameNumber, entries);
            onSuccess(`Game ${editingGameNumber} scores saved`);
            setShowEditScores(false);
            loadGroupScores(currentGroup);
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to save scores');
        }
    };

    const goBack = () => {
        if (view === 'group') {
            loadGroups(currentRound.id);
        } else if (view === 'round') {
            loadRounds(currentTournament.id);
        } else if (view === 'tournament') {
            setView('list');
            loadTournaments();
        }
    };

    // Render Tournament List
    if (view === 'list') {
        return (
            <div>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Award className="w-6 h-6 text-yellow-500" />
                            Tournament
                        </h2>
                        <p className="text-sm text-zinc-400">Kelola tournament dan babak pertandingan</p>
                    </div>
                    <Button 
                        onClick={() => setShowCreateTournament(true)}
                        className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Buat Tournament
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><span className="spinner" /></div>
                ) : tournaments.length === 0 ? (
                    <div className="empty-state">
                        <Award className="empty-state-icon mx-auto" />
                        <p>Belum ada tournament</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {tournaments.map(t => (
                            <div key={t.id} className="stat-card p-4 flex items-center justify-between" data-testid={`tournament-card-${t.id}`}>
                                <div 
                                    className="flex-1 cursor-pointer hover:bg-zinc-800/50 -m-2 p-2 rounded"
                                    onClick={() => loadRounds(t.id)}
                                    data-testid={`tournament-click-${t.id}`}
                                >
                                    <h3 className="text-lg font-semibold text-white">{t.name}</h3>
                                    <p className="text-sm text-zinc-400">{t.round_count || 0} babak</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => loadRounds(t.id)}
                                        className="text-neon-green"
                                        data-testid={`tournament-view-${t.id}`}
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteTournament(t.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Tournament Modal */}
                <Dialog open={showCreateTournament} onOpenChange={setShowCreateTournament}>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle>Buat Tournament Baru</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Nama Tournament</Label>
                                <Input
                                    value={tournamentName}
                                    onChange={(e) => setTournamentName(e.target.value)}
                                    placeholder="Contoh: MJ Chinese New Year"
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>
                            <Button onClick={handleCreateTournament} className="w-full bg-neon-green text-void-black">
                                Buat Tournament
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Render Tournament Detail (Rounds)
    if (view === 'tournament') {
        return (
            <div>
                <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" size="sm" onClick={goBack} className="border-zinc-700 text-zinc-300 hover:text-white">
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Kembali
                    </Button>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-white">{currentTournament?.name}</h2>
                        <p className="text-sm text-zinc-400">Kelola babak pertandingan</p>
                    </div>
                    <Button 
                        onClick={() => setShowCreateRound(true)}
                        className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Babak
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><span className="spinner" /></div>
                ) : rounds.length === 0 ? (
                    <div className="empty-state">
                        <Trophy className="empty-state-icon mx-auto" />
                        <p>Belum ada babak</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {rounds.map((r, idx) => (
                            <div key={r.id} className="stat-card p-4 flex items-center justify-between">
                                <div 
                                    className="flex-1 cursor-pointer"
                                    onClick={() => loadGroups(r.id)}
                                >
                                    <h3 className="text-lg font-semibold text-white">
                                        <span className="text-yellow-500 mr-2">#{idx + 1}</span>
                                        {r.name}
                                    </h3>
                                    <p className="text-sm text-zinc-400">{r.group_count || 0} grup</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => loadGroups(r.id)}
                                        className="text-neon-green"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteRound(r.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Round Modal */}
                <Dialog open={showCreateRound} onOpenChange={setShowCreateRound}>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle>Tambah Babak Baru</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Nama Babak</Label>
                                <Input
                                    value={roundName}
                                    onChange={(e) => setRoundName(e.target.value)}
                                    placeholder="Contoh: Penyisihan, Semifinal, Final"
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>
                            <Button onClick={handleCreateRound} className="w-full bg-neon-green text-void-black">
                                Tambah Babak
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Render Round Detail (Groups)
    if (view === 'round') {
        return (
            <div>
                <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" size="sm" onClick={goBack} className="border-zinc-700 text-zinc-300 hover:text-white">
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Kembali
                    </Button>
                    <div className="flex-1">
                        <p className="text-sm text-zinc-400">{currentTournament?.name}</p>
                        <h2 className="text-xl font-bold text-white">{currentRound?.name}</h2>
                    </div>
                    <Button 
                        onClick={() => setShowCreateGroup(true)}
                        className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Grup
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><span className="spinner" /></div>
                ) : groups.length === 0 ? (
                    <div className="empty-state">
                        <Users className="empty-state-icon mx-auto" />
                        <p>Belum ada grup</p>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                        {groups.map(g => (
                            <div key={g.id} className="stat-card p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold text-yellow-500">Grup {g.name}</h3>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => loadGroupScores(g)}
                                            className="text-neon-green"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteGroup(g.id)}
                                            className="text-red-400"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-sm text-zinc-400 mb-2">
                                    {g.members_data?.length || 0} pemain • {g.num_games} game
                                </p>
                                {g.members_data?.length > 0 && (
                                    <div className="text-xs text-zinc-500">
                                        {g.members_data.map(m => m.username).join(', ')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Group Modal */}
                <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle>Tambah Grup Baru</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Nama Grup</Label>
                                <Input
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    placeholder="Contoh: A, B, C, D"
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>
                            <div>
                                <Label>Jumlah Game</Label>
                                <Input
                                    type="number"
                                    value={numGames}
                                    onChange={(e) => setNumGames(parseInt(e.target.value) || 1)}
                                    min={1}
                                    max={20}
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>
                            <Button onClick={handleCreateGroup} className="w-full bg-neon-green text-void-black">
                                Tambah Grup
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Render Group Detail (Scores Table)
    if (view === 'group' && groupScores) {
        const { group, members, scores, totals } = groupScores;
        const gameColumns = Array.from({ length: group.num_games }, (_, i) => i + 1);
        
        // Get score for a specific user and game
        const getScore = (userId, gameNum) => {
            const gameScore = scores.find(s => s.game_number === gameNum);
            const entry = gameScore?.entries?.find(e => e.user_id === userId);
            return entry?.score;
        };

        // Sort members by total score
        const sortedMembers = [...members].sort((a, b) => (totals[b.id] || 0) - (totals[a.id] || 0));

        return (
            <div>
                <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" size="sm" onClick={goBack} className="border-zinc-700 text-zinc-300 hover:text-white">
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Kembali
                    </Button>
                    <div className="flex-1">
                        <p className="text-sm text-zinc-400">{currentTournament?.name} • {currentRound?.name}</p>
                        <h2 className="text-xl font-bold text-white">Grup {group.name}</h2>
                    </div>
                    <Button 
                        onClick={() => setShowAddMember(true)}
                        className="bg-neon-green hover:bg-neon-green/90 text-void-black"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Pemain
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><span className="spinner" /></div>
                ) : members.length === 0 ? (
                    <div className="empty-state">
                        <Users className="empty-state-icon mx-auto" />
                        <p>Belum ada pemain di grup ini</p>
                    </div>
                ) : (
                    <div className="stat-card overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-zinc-700">
                                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Pemain</th>
                                    {gameColumns.map(num => (
                                        <th key={num} className="text-center py-3 px-4 text-zinc-400 font-medium">
                                            <div className="flex flex-col items-center gap-1">
                                                <span>Game {num}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEditScores(num)}
                                                    className="text-xs text-neon-green h-6 px-2"
                                                >
                                                    <Edit2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </th>
                                    ))}
                                    <th className="text-center py-3 px-4 text-yellow-500 font-bold">Total</th>
                                    <th className="w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMembers.map((m, idx) => (
                                    <tr key={m.id} className={`border-b border-zinc-800 ${idx === 0 ? 'bg-yellow-500/10' : ''}`}>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-2">
                                                {idx === 0 && <Trophy className="w-4 h-4 text-yellow-500" />}
                                                <span className="text-white font-medium">{m.username}</span>
                                            </div>
                                        </td>
                                        {gameColumns.map(num => {
                                            const score = getScore(m.id, num);
                                            return (
                                                <td key={num} className="text-center py-3 px-4">
                                                    {score !== undefined ? (
                                                        <span className={score > 0 ? 'text-neon-green' : score < 0 ? 'text-red-400' : 'text-zinc-400'}>
                                                            {score > 0 ? '+' : ''}{score}
                                                        </span>
                                                    ) : (
                                                        <span className="text-zinc-600">-</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="text-center py-3 px-4">
                                            <span className={`font-bold ${(totals[m.id] || 0) > 0 ? 'text-neon-green' : (totals[m.id] || 0) < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                                                {(totals[m.id] || 0) > 0 ? '+' : ''}{totals[m.id] || 0}
                                            </span>
                                        </td>
                                        <td className="py-3 px-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveMember(m.id)}
                                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Add Member Modal */}
                <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle>Tambah Pemain</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Pilih Pemain</Label>
                                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                        <SelectValue placeholder="Pilih pemain..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                        {users.filter(u => !members.some(m => m.id === u.id)).map(u => (
                                            <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={handleAddMember} className="w-full bg-neon-green text-void-black">
                                Tambah Pemain
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Edit Scores Modal */}
                <Dialog open={showEditScores} onOpenChange={setShowEditScores}>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle>Input Score Game {editingGameNumber}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <p className="text-sm text-zinc-400">Masukkan score untuk setiap pemain (bisa positif atau negatif)</p>
                            {members.map(m => (
                                <div key={m.id} className="flex items-center gap-4">
                                    <Label className="w-24 text-white">{m.username}</Label>
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        value={scoreInputs[m.id] || '0'}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // Allow empty, minus sign, or valid number
                                            if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
                                                setScoreInputs({...scoreInputs, [m.id]: val});
                                            }
                                        }}
                                        className="bg-zinc-800 border-zinc-700 flex-1"
                                        placeholder="Contoh: 10 atau -5"
                                    />
                                </div>
                            ))}
                            <Button onClick={handleSaveScores} className="w-full bg-neon-green text-void-black">
                                <Save className="w-4 h-4 mr-2" />
                                Simpan Score
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return null;
};

export default TournamentManager;
